
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { PhaseStatus } from '@/components/onboarding/PhaseStatusIndicator';

export type Phase = {
  number: number;
  status: PhaseStatus;
  title: string;
  description: string;
};

/**
 * Generates a unique link for the onboarding form
 * @returns The generated onboarding link
 */
export const generateOnboardingLink = (): string => {
  const uniqueId = uuidv4();
  const baseUrl = window.location.origin;
  return `${baseUrl}/onboarding/${uniqueId}`;
};

/**
 * Determines the phase status based on the status string
 */
export const getPhaseStatus = (status?: string): PhaseStatus => {
  if (!status) return "pending";
  switch (status) {
    case "completed": return "completed";
    case "in-progress": return "in-progress";
    case "error": return "error";
    default: return "pending";
  }
};

/**
 * Gets all phases data for a form
 */
export const getPhaseData = (formData: any): Phase[] => {
  if (!formData) return [];
  
  return [
    {
      number: 1,
      status: getPhaseStatus(formData.status_phase_1),
      title: "Form Submission",
      description: "Initial submission of creator information",
    },
    {
      number: 2,
      status: getPhaseStatus(formData.status_phase_2),
      title: "Application Review",
      description: "Team reviews your application",
    },
    {
      number: 3,
      status: getPhaseStatus(formData.status_phase_3),
      title: "Invoice Generation",
      description: "Creation of invoice for compensation",
    },
    {
      number: 4,
      status: getPhaseStatus(formData.status_phase_4),
      title: "Test Transaction",
      description: "Small test payment to verify wallet",
    },
    {
      number: 5,
      status: getPhaseStatus(formData.status_phase_5),
      title: "Test Confirmation",
      description: "Verification of successful test payment",
    },
    {
      number: 6,
      status: getPhaseStatus(formData.status_phase_6),
      title: "Final Payment",
      description: "Processing of full compensation amount",
    },
    {
      number: 7,
      status: getPhaseStatus(formData.status_phase_7),
      title: "Completion",
      description: "All onboarding steps successfully completed",
    },
  ];
};

/**
 * Determine current phase based on status values
 */
export const getCurrentPhase = (formData: any): number => {
  if (!formData) return 1;
  
  if (formData.status_phase_7 === "completed") return 7;
  if (formData.status_phase_6 === "completed" || formData.status_phase_6 === "in-progress") return 6;
  if (formData.status_phase_5 === "completed" || formData.status_phase_5 === "in-progress") return 5;
  if (formData.status_phase_4 === "completed" || formData.status_phase_4 === "in-progress") return 4;
  if (formData.status_phase_3 === "completed" || formData.status_phase_3 === "in-progress") return 3;
  if (formData.status_phase_2 === "completed" || formData.status_phase_2 === "in-progress") return 2;
  return 1;
};

/**
 * Check if a specific form step should be available based on the current phase
 */
export const isStepAvailableForPhase = (step: number, currentPhase: number): boolean => {
  // Step 1-3 (Creator, Content, Additional) are always available
  if (step <= 3) return true;
  
  // Step 4 (Invoice) is only available from phase 3+
  if (step === 4) return currentPhase >= 3;
  
  // Step 5 (Review) is always available
  if (step === 5) return true;
  
  return false;
};

/**
 * Validates form data
 * @param formData The form data to validate
 * @returns An object containing validation errors if any
 */
export const validateFormData = (formData: any) => {
  const errors: Record<string, string> = {};
  
  if (!formData.kol_name) {
    errors.kol_name = 'KOL name is required';
  }
  
  // Add more validations as needed
  
  return Object.keys(errors).length > 0 ? errors : null;
};

/**
 * Saves form data to the database
 * @param formData The form data to save
 * @returns The saved form data
 */
export const saveFormData = async (formData: any) => {
  try {
    const { data, error } = await supabase
      .from('onboarding_forms')
      .insert([formData])
      .select()
      .single();
    
    if (error) throw error;
    
    return { data, error: null };
  } catch (error: any) {
    console.error('Error saving form data:', error);
    return { data: null, error };
  }
};

/**
 * Fetches form data by its ID
 * @param formId The ID of the form
 * @returns The fetched form data
 */
export const getFormById = async (formId: string) => {
  try {
    const { data, error } = await supabase
      .from('onboarding_forms')
      .select('*')
      .eq('id', formId)
      .single();
    
    if (error) throw error;
    
    return { data, error: null };
  } catch (error: any) {
    console.error('Error fetching form data:', error);
    return { data: null, error };
  }
};

/**
 * Fetches form data by its unique link
 * @param uniqueLink The unique link of the form
 * @returns The fetched form data
 */
export const getFormByUniqueLink = async (uniqueLink: string) => {
  try {
    // First try to find the form_id from onboarding_links table
    const { data: linkData, error: linkError } = await supabase
      .from('onboarding_links')
      .select('form_id')
      .eq('unique_link', uniqueLink)
      .maybeSingle();
    
    if (linkError) throw linkError;
    
    if (!linkData || !linkData.form_id) {
      return { data: null, error: new Error('No form found for this link') };
    }
    
    // Then get the form data using the form_id
    const { data: formData, error: formError } = await supabase
      .from('onboarding_forms')
      .select('*')
      .eq('id', linkData.form_id)
      .single();
    
    if (formError) throw formError;
    
    return { data: formData, error: null };
  } catch (error: any) {
    console.error('Error fetching form data:', error);
    return { data: null, error };
  }
};
